// 개시 하한 — **자동 조정과 직접 입력 차단은 다른 일이다**
//
// 조건은 `open ≥ now + 6초` 하나인데 처리가 둘이다. 같은 처리로 묶으면 둘 다 틀린다.
//
//   - sub 가 붙어 가장 이른 개시가 하한 안으로 들어오는 것은 **사람이 하지 않은 일**이다.
//     사람은 착탄 간격만 정했고 하한을 어긴 것은 계산의 결과다 → 조용히 평행이동한다.
//   - 시간 손잡이에 `00:05` 를 치는 것은 **사람이 한 일**이다. 6초로 보정하면 사람이
//     적은 값과 시스템이 쓴 값이 갈리고, 갈린 것을 알려 줄 사람이 이 프로젝트에는
//     없다 → 경고와 함께 막는다.
//
// 하나로 묶으면 앞의 것은 차단돼 시퀀스를 못 만들고 뒤의 것은 조용히 보정돼 틀린 값이
// 산다. 그래서 함수를 둘로 가른다.
//
// ── 말투는 화면이 정한다 (2026-08-28 T3 개정) ─────────────────────────────
//
// 이 파일이 한국어 거절 문장을 만들던 시절이 있었다. 서버가 그 문장을 그대로 실어
// 보냈고, 이름이 없어 화면은 자기 언어로 옮길 길이 없었다. 그래서 영어 화면을 쓰는
// 사람이 개시 하한을 어기면 한국어 한 줄을 읽었다.
//
// 이제 여기서 나가는 것은 **거절의 이름과 값 하나**뿐이다. 문장은 i18n 표가 들고 화면이
// 자기 언어로 만든다. 이름이 둘인 것은 판정이 둘이어서가 아니라 **문맥이 둘**이어서다.
// 판정식은 여전히 `open ≥ now + 6초` 하나이고, 그 하나를 네 자리가 함께 쓴다.

import {
  addDuration,
  type Duration,
  duration,
  type Instant,
  instant,
  toMillis,
} from '../time/brand.ts'
import { earliestOpen, type SolvedRally } from './attack.ts'

/**
 * 개시 하한 6초 = 개시 직전 신호 구간 5초 + 여유 1초.
 *
 * 신호가 5초짜리라 그보다 짧으면 신호를 끝까지 들려줄 수 없고, roster 는 "미리 셀 수
 * 있는 종점" 없이 버튼을 누르게 된다.
 */
export const LEAD_FLOOR: Duration = duration(6_000)

/** rally 대기 시간의 네 선택지. 기본은 5분이다. */
export const RALLYING_OPTIONS: readonly Duration[] = [
  duration(60_000),
  duration(120_000),
  duration(300_000),
  duration(600_000),
]

/**
 * 하한을 어긴 자리의 문맥. **사람이 친 값인가, 계산된 결과인가.**
 *
 * 두 자리에서 재는 수는 둘 다 「개시 여유」로 같지만, 사람에게 할 말은 다르다. 손잡이
 * 직접 입력은 사람이 친 값을 되돌려 주면 되고, 진행 중 시퀀스에 줄을 붙이거나 고치는
 * 자리의 값은 offset 과 rallying 과 행군에서 계산된 것이라 사람이 친 적이 없다.
 */
export type FloorContext = 'handle' | 'derived'

/**
 * 하한 판정의 결과. **판별 유니온이다.**
 *
 * 거절이면 이름과 값이 반드시 있다는 것을 타입이 진다. 둘을 선택 항목으로 두면 호출부마다
 * `?? '기본 이름'` 을 적게 되고, 그 기본값은 실제로 쓰이지 않으면서 「이름이 없을 수도
 * 있다」는 거짓을 코드에 남긴다.
 */
export type HandleVerdict =
  | { accepted: true }
  | {
      accepted: false
      /**
       * 거절 사유의 **이름**이다. 문장이 아니다.
       *
       * 화면이 이 이름으로 자기 언어의 문장을 만들고, 서버는 같은 이름으로 한국어를 채워
       * 응답에 함께 싣는다(이 앱의 화면이 아닌 것이 붙었을 때 빈 응답을 주지 않으려는
       * 것이다). 코어가 문장을 만들던 시절에는 그 한국어가 영어 화면에도 그대로 떴다.
       */
      code: 'leadFloorHandle' | 'leadFloorDerived'
      /** 그 문장이 받는 값 하나. 하한을 어긴 개시 여유를 초로 적은 것이다. */
      value: string
    }

/**
 * 개시 여유를 사람이 읽는 초로 적는다. **0.1초로 버린다.**
 *
 * `formatDuration` 을 쓰지 않는 이유는 그쪽이 0.1초 격자를 벗어난 값을 거절하기
 * 때문이다. 여기 오는 값에는 `open - now` 처럼 격자를 안 지키는 것이 섞이고, 거절
 * 응답을 만들다가 예외가 나면 400 이 500 이 된다.
 *
 * 버리는 쪽으로 자르는 것은 어긴 값이 실제보다 넉넉해 보이지 않게 하려는 것이다.
 * 버림은 언제나 원값 이하라 화면이 서버보다 관대해지는 방향으로는 틀리지 않는다.
 */
function seconds(millis: number): string {
  return String(Math.floor(millis / 100) / 10)
}

/**
 * 개시 여유가 하한을 지키는지 본다. **보정이 아니라 차단이다.**
 *
 * 경계값 6초는 허용한다. 경계를 빼면 이 판정은 절반만 한 것이다.
 *
 * **문맥을 인자로 받는다.** 한 문장을 네 자리가 나눠 쓰면 계산으로 값이 정해지는 뒤의
 * 둘에서 화면이 사람이 넣은 적 없는 숫자를 「넣은 값」이라 부른다. 문맥을 이름으로
 * 옮기는 일을 여기서 하는 것은, 호출부가 각자 이름을 적으면 두 자리씩 같은 글자를
 * 손으로 맞춰야 하고 그 손이 언젠가 틀리기 때문이다.
 */
export function checkHandleInput(handle: Duration, context: FloorContext): HandleVerdict {
  const millis = toMillis(handle)
  if (millis >= toMillis(LEAD_FLOOR)) return { accepted: true }
  return {
    accepted: false,
    code: context === 'handle' ? 'leadFloorHandle' : 'leadFloorDerived',
    value: seconds(millis),
  }
}

export interface FloorShift {
  /** 시퀀스 전체가 미뤄진 양. 이미 하한을 지키면 0 이다. */
  shift: Duration
  rallies: SolvedRally[]
}

/**
 * 가장 이른 개시가 하한 안으로 들어왔으면 **시퀀스 전체를 평행이동한다.**
 *
 * 강체로 미는 것이 핵심이다. 어긴 rally 하나만 밀면 CT 가 설계한 착탄 간격이 조용히
 * 무너지는데, 그 무너짐이 이 제품이 없애려는 실패와 같은 종류다.
 */
export function shiftToFloor(rallies: readonly SolvedRally[], now: Instant): FloorShift {
  if (rallies.length === 0) return { shift: duration(0), rallies: [] }

  const floor = toMillis(now) + toMillis(LEAD_FLOOR)
  const shift = duration(Math.max(0, floor - toMillis(earliestOpen(rallies))))
  if (toMillis(shift) === 0) return { shift, rallies: [...rallies] }

  return {
    shift,
    rallies: rallies.map((rally) => ({
      ...rally,
      open: addDuration(rally.open, shift),
      impact: addDuration(rally.impact, shift),
    })),
  }
}

export interface RallyingOptionInput {
  /** 진행 중 시퀀스의 착탄 원점. 이미 고정돼 있다. */
  impactMain: Instant
  /** 붙이려는 rally 의 착탄축 간격. */
  offset: Duration
  /** 붙이려는 rally 를 맡을 roster 의 행군 시간. */
  marching: Duration
  now: Instant
}

export interface RallyingOptionPreview {
  rallying: Duration
  open: Instant
  /** 개시 여유 = 개시 시각 − 지금. 하한과 비교되는 값이다. */
  lead: Duration
  feasible: boolean
  /** 이 옵션으로 하한을 정확히 만족시키는 최소 착탄축 간격. */
  minimumOffset: Duration
}

/**
 * 네 옵션의 결과를 **동시에** 낸다.
 *
 * 네 값이 서로 같게 나오면 rallying 을 식에 안 넣은 것이고, 그것이 이 함수가 잡아야 할
 * 결함이다. 불가 옵션에 "얼마면 되는가" 를 함께 돌려주는 이유는, 그것이 없으면 화면이
 * 불가만 보여 주고 CT 가 간격을 손으로 더듬게 되기 때문이다 — 그 더듬는 일이 이 앱이
 * 없애려는 수기 작업 그 자체다.
 */
export function previewRallyingOptions(input: RallyingOptionInput): RallyingOptionPreview[] {
  const impactMain = toMillis(input.impactMain)
  const offset = toMillis(input.offset)
  const marching = toMillis(input.marching)
  const now = toMillis(input.now)
  const floor = toMillis(LEAD_FLOOR)

  return RALLYING_OPTIONS.map((rallying) => {
    const rallyingMillis = toMillis(rallying)
    const openMillis = impactMain + offset - rallyingMillis - marching
    const lead = duration(openMillis - now)
    return {
      rallying,
      open: instant(openMillis),
      lead,
      feasible: toMillis(lead) >= floor,
      minimumOffset: duration(now + floor - impactMain + rallyingMillis + marching),
    }
  })
}
